import { PopoverPosition } from '@/_constants';
import { NovatiqPopoverHelper } from '@/_helpers/novatiq-popover.helper';
import { NgTemplateOutlet } from '@angular/common';
import { Component, effect, ElementRef, inject, input, TemplateRef, viewChild } from '@angular/core';
import { Popover } from 'primeng/popover';

export type NovatiqPopoverPanelPosition = 'default' | 'top-left';

@Component({
  selector: 'app-novatiq-popover',
  templateUrl: './novatiq-popover.component.html',
  styleUrl: './novatiq-popover.component.scss',
  imports: [Popover, NgTemplateOutlet]
})
export class NovatiqPopoverComponent {
  readonly popTemplate = input<TemplateRef<unknown> | null>(null);
  readonly position = input(PopoverPosition.Bottom);
  readonly container = input<'body' | HTMLElement>('body');
  readonly closeOnOutsideClick = input(false);
  readonly adaptivePosition = input(false);
  readonly panelPosition = input<NovatiqPopoverPanelPosition>('default');
  readonly id = input.required<string>();

  private readonly pPopover = viewChild.required<Popover>('pPopover');
  private readonly triggerRef = viewChild.required('trigger', { read: ElementRef<HTMLElement> });

  private readonly popoverHelper = inject(NovatiqPopoverHelper);

  protected get popTemplateRef(): TemplateRef<unknown> | null {
    return this.popTemplate();
  }

  protected get appendTarget(): 'body' | HTMLElement {
    return this.container() === 'body' ? 'body' : this.container();
  }

  protected get popoverStyleClass(): string {
    const base = 'client-mgmt-novatiq-popover-panel';
    return this.panelPosition() === 'top-left' ? `${base} ${base}--top-left` : base;
  }

  close(): void {
    this.popoverHelper.close();
  }

  onPrimePopoverShow(): void {
    if (this.panelPosition() !== 'top-left') {
      return;
    }
    requestAnimationFrame(() => requestAnimationFrame(() => this.adjustTopLeftPosition()));
  }

  constructor() {
    effect(() => {
      const activeId = this.popoverHelper.popoverId();
      const popover = this.pPopover();
      const trigger = this.triggerRef()?.nativeElement;

      if (activeId === this.id()) {
        if (trigger) {
          popover.show(null, trigger);
        }
      } else {
        popover.hide();
      }
    });
  }

  private adjustTopLeftPosition(): void {
    const panel = this.pPopover()?.container;
    const target = this.triggerRef()?.nativeElement;
    if (!panel || !target) {
      return;
    }
    const gutter = 8;
    const t = target.getBoundingClientRect();
    const ph = panel.offsetHeight;
    const scrollTop = window.scrollY;
    const scrollLeft = window.scrollX;
    let top = t.top + scrollTop - ph - gutter;
    const left = t.left + scrollLeft;
    const showAbove = top >= scrollTop;
    if (!showAbove) {
      top = t.bottom + scrollTop + gutter;
    }
    panel.style.top = `${top}px`;
    panel.style.left = `${left}px`;
    panel.style.marginTop = '0';
    panel.style.marginBottom = '0';
    panel.classList.toggle('p-popover-flipped', showAbove);
  }
}
